/**
 * A3 — On-Open-Auto-Sync-Scheduler (Postfach/Ordner geöffnet → synchronisieren).
 *
 * Reine, testbare Debounce-/Coalescing-Logik, unabhängig von React: Beim Öffnen
 * bzw. schnellen Durchklicken von Postfächern/Ordnern feuert `schedule` je Öffnen,
 * es läuft aber NUR EIN `sync`-Aufruf für das ZULETZT geöffnete Postfach — kein
 * Doppel-Sync bei schnellem Wechsel (Review 2026-07-06, Punkt A3). `cancel`
 * verwirft einen noch nicht ausgelösten Lauf (React-Cleanup/Unmount).
 *
 * Die Timer-Funktionen sind injizierbar (Default = globale `setTimeout`/
 * `clearTimeout`), damit die Debounce-Logik deterministisch testbar bleibt.
 */

/** Debounce-Fenster für den On-Open-Auto-Sync (ms). */
export const ON_OPEN_SYNC_DEBOUNCE_MS = 400;

type TimerHandle = ReturnType<typeof setTimeout>;

export interface OnOpenSyncScheduler {
    /** Öffnen eines Postfachs/Ordners registrieren → debounced Sync. */
    schedule(mailbox: string): void;
    /** Anstehenden (noch nicht ausgelösten) Sync verwerfen (Cleanup/Unmount). */
    cancel(): void;
}

export interface OnOpenSyncOptions {
    /** Wird nach dem Debounce-Fenster mit dem zuletzt geöffneten Postfach aufgerufen. */
    sync: (mailbox: string) => void;
    /** Debounce-Fenster (Default `ON_OPEN_SYNC_DEBOUNCE_MS`). */
    delayMs?: number;
    setTimeoutFn?: (fn: () => void, ms: number) => TimerHandle;
    clearTimeoutFn?: (handle: TimerHandle) => void;
}

export function createOnOpenSyncScheduler(opts: OnOpenSyncOptions): OnOpenSyncScheduler {
    const delay = opts.delayMs ?? ON_OPEN_SYNC_DEBOUNCE_MS;
    const setT = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
    const clearT = opts.clearTimeoutFn ?? ((h) => clearTimeout(h));

    let handle: TimerHandle | null = null;
    let pending: string | null = null;

    const cancel = (): void => {
        if (handle !== null) {
            clearT(handle);
            handle = null;
        }
        pending = null;
    };

    const schedule = (mailbox: string): void => {
        pending = mailbox;
        // Vorheriges, noch nicht ausgelöstes Fenster verwerfen → Coalescing.
        if (handle !== null) clearT(handle);
        handle = setT(() => {
            handle = null;
            const mb = pending;
            pending = null;
            if (mb) opts.sync(mb);
        }, delay);
    };

    return { schedule, cancel };
}
